// @ts-nocheck
import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import ProfilePictureRounded from '../components/ProfilePictureRounded';
import OrganizationUpdateMemberShip from '../components/Organization/OrganizationUpdateMemberShip';
import { PageContainer, PageHeader } from "../components/AssetBreadCrumb";
import { PageBackButton } from "../components/PageChrome";
import { Skeleton, SkeletonGroup } from '../components/Skeleton';
import SettingsRow, { SettingsGroup } from '../components/Settings/SettingsRow';
import { ReactComponent as GlobeIcon } from "../assets/icons/globe-02.svg";
import { fetchOrganizationPublicProfile } from '../store/publicProfileSlice';
import { setModalState, modalViewsConst } from '../store/modalSlice';

const OrganizationPage = () => {
    const { organizationId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const orgProfiles = useSelector((state) => state.publicProfile.orgProfiles);

    const { loaded, data } = user.organizations;
    const organizationData = data?.[organizationId];

    // The org owner (organizationData.owner is a User id) or a platform Admin may edit the organization's public profile.
    const isPlatformAdmin = user?.details?.groups?.includes('Admin');
    const isOrgOwner = organizationData && organizationData.owner === user?.permissionDetails?.id;
    const canEditOrgProfile = isPlatformAdmin || isOrgOwner;

    // Fetch the org's public profile so we can flag when one hasn't been created yet.
    useEffect(() => {
        if (organizationId && canEditOrgProfile) {
            dispatch(fetchOrganizationPublicProfile(organizationId));
        }
    }, [dispatch, organizationId, canEditOrgProfile]);

    // Key present but falsy => fetched and confirmed absent (vs. undefined = not fetched yet).
    const orgProfileFetched = orgProfiles && Object.prototype.hasOwnProperty.call(orgProfiles, organizationId);
    const hasNoOrgPublicProfile = orgProfileFetched && !orgProfiles[organizationId];

    // Owner / Admin only — the Organization record's @auth lets nobody else write profilePictureUrl
    const handleChangePicture = () => {
        if (!canEditOrgProfile || !organizationData) return;
        dispatch(
            setModalState({
                modalOpen: true,
                modalView: modalViewsConst.organizationImageUpload,
                properties: {
                    id: organizationData.id,
                    organizationId: organizationData.id,
                    name: organizationData.name,
                    imageUrl: organizationData.profilePictureUrl,
                },
            })
        );
    };

    if(!loaded) {
        return (
            <PageContainer>
                <div className="mx-auto w-full max-w-3xl">
                    <div className="eai-panel flex items-center gap-5">
                        <Skeleton className="h-20 w-20" radius="full" />
                        <div className="min-w-0 flex-1">
                            <Skeleton className="h-6 w-48" radius="md" />
                            <Skeleton className="mt-2 h-3.5 w-64" />
                        </div>
                    </div>
                    <SkeletonGroup count={1} label="Loading organization" className="mt-8">
                        <div className="eai-card divide-y divide-eai-border overflow-hidden">
                            <div className="flex items-center gap-4 px-5 py-4">
                                <Skeleton className="h-9 w-9" radius="full" />
                                <Skeleton className="h-4 w-40" radius="md" />
                            </div>
                        </div>
                    </SkeletonGroup>
                </div>
            </PageContainer>
        );
    } else if (loaded && !organizationData) {
        return (
            <PageContainer>
                <div className="mx-auto w-full max-w-3xl">
                    <PageHeader
                        eyebrow="Organization"
                        title="Not found"
                        action={
                            <PageBackButton label="Back to Settings" onClick={() => navigate('/home/settings')} />
                        }
                    />
                    <div className="eai-panel eai-description-sm">Could not load organization.</div>
                </div>
            </PageContainer>
        );
    }

    const adminMember = organizationData.members.find((member) => organizationData.owner === member.id);
    // Owner first — the old page gave them a card of their own, which listed them twice
    const orderedMembers = [
        ...(adminMember ? [adminMember] : []),
        ...organizationData.members.filter((m) => m.id !== organizationData.owner),
    ];

    return (
        <PageContainer>
            {/* Same column as Settings — this page is reached from it */}
            <div className="mx-auto w-full max-w-3xl">
                {/* An organization is an entity with a picture, so it opens on the group page's masthead rather than a PageHeader — the name is the h1, not a panel heading */}
                <div className="mb-8 flex flex-col items-start gap-5 sm:flex-row sm:items-center">
                    <ProfilePictureRounded
                        onClick={canEditOrgProfile ? handleChangePicture : undefined}
                        testId="organization-profile-picture"
                        className={`shrink-0 border border-eai-border [--profile-diameter:80px] sm:[--profile-diameter:96px]${canEditOrgProfile ? ' cursor-pointer transition-colors hover:border-eai-border-hover' : ''}`}
                        pictureURL={organizationData.profilePictureUrl}
                        diameter={96}
                    />
                    <div className="min-w-0 flex-1">
                        <div className="mb-1.5 flex items-center">
                            <span className="eai-slash mr-1.5 text-eai-body-sm leading-none">{"//"}</span>
                            <span className="eai-eyebrow">Organization</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="eai-page-title min-w-0 truncate">{organizationData.name}</h1>
                            <span className="eai-tag-pill-sm shrink-0">
                                {organizationData.members.length}{' '}
                                {organizationData.members.length === 1 ? 'member' : 'members'}
                            </span>
                        </div>
                        {organizationData.description && (
                            <p className="eai-description-sm mt-1">{organizationData.description}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate('/home/settings')}
                        className="eai-btn-outline w-full shrink-0 sm:w-auto"
                    >
                        Back to Settings
                    </button>
                </div>

                <div className="flex flex-col gap-8">
                    <SettingsGroup title="Members">
                        {orderedMembers.map((member) => (
                            <SettingsRow
                                key={member.id}
                                title={member.name}
                                description={member.email}
                                leading={
                                    <ProfilePictureRounded
                                        className="shrink-0 border border-eai-border"
                                        diameter={36}
                                        pictureURL={member.profilePictureUrl}
                                    />
                                }
                                trailing={
                                    member.id === organizationData.owner ? (
                                        <span className="eai-tag-pill-sm shrink-0">Owner</span>
                                    ) : undefined
                                }
                            />
                        ))}
                    </SettingsGroup>

                    {/* Organization Public Profile — editable by the org owner or a platform Admin */}
                    {canEditOrgProfile && (
                        <SettingsGroup title="Visibility">
                            <SettingsRow
                                title="Public Profile"
                                description={
                                    <>
                                        Choose what details are shown publicly
                                        {hasNoOrgPublicProfile && (
                                            <span className="font-medium text-eai-danger-text"> — none set up yet</span>
                                        )}
                                    </>
                                }
                                icon={<GlobeIcon aria-hidden className="h-4 w-4 [&_path]:stroke-current" />}
                                onClick={() => navigate(`/home/organization/${organizationId}/public-profile`)}
                            />
                        </SettingsGroup>
                    )}

                    {/* Admin Management Section */}
                    {user?.details?.groups?.includes('Admin') && (
                        <section className="eai-panel">
                            <OrganizationUpdateMemberShip organizationId={organizationId} />
                        </section>
                    )}
                </div>
            </div>
        </PageContainer>
    );
};

export default OrganizationPage;